import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuthContext } from "@/hooks/useAuthContext";
import { Navigate, useSearchParams } from "react-router";
import { HelpCircle, Eye, EyeOff } from "lucide-react";
import { Link } from "react-router";
import { useGetLatestTermsOfServiceQuery, useUpdateUserTosAcceptanceMutation } from "@/redux/api/legal.rtkq";

const LoginPage = () => {
  const { user, loadingUserData, signIn, signUp, signInWithGoogle, resetPassword } = useAuthContext();
  const [isSignUp, setIsSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    fullName: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Get latest ToS version for acceptance
  const { data: latestTos } = useGetLatestTermsOfServiceQuery(undefined, {
    skip: !isSignUp, // Only fetch when in signup mode
  });
  const [updateTosAcceptance] = useUpdateUserTosAcceptanceMutation();

  // Redirect if user is already authenticated.
  // Default to `/` (HomePage) — not an entitlement-gated app or /super-admin,
  // so Free / no-apps / non–super-admin users are not bounced to /unauthorized.
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get("redirect");
  const unsafeRedirect =
    !redirectParam ||
    redirectParam === "/unauthorized" ||
    redirectParam === "/super-admin" ||
    redirectParam.startsWith("/super-admin/");
  const redirectTo = unsafeRedirect ? "/" : redirectParam;

  if (!loadingUserData && user) {
    return <Navigate to={redirectTo} replace />;
  }

  if (loadingUserData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">טוען...</div>
      </div>
    );
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
    setSuccessMessage(null);
  };

  // Store ToS acceptance via backend after signup
  const storeToSAcceptance = async (tosVersion: string) => {
    try {
      await updateTosAcceptance({
        version: tosVersion,
        ipAddress: undefined,
      }).unwrap();
      console.log("ToS acceptance stored successfully");
    } catch (error) {
      console.error("Failed to store ToS acceptance:", error);
      // TosGuard will show modal if needed
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      if (isSignUp) {
        if (!latestTos?.version) {
          setError("לא ניתן לטעון את תנאי השימוש. אנא נסה שוב.");
          setIsSubmitting(false);
          return;
        }

        const result = await signUp({
          email: formData.email,
          password: formData.password,
          displayName: formData.fullName,
        });

        if (result.error) {
          setError(getHebrewErrorMessage(result.error.message));
        } else if (result.user) {
          storeToSAcceptance(latestTos.version);
        }
      } else {
        const result = await signIn(formData.email, formData.password);
        if (result.error) {
          setError(getHebrewErrorMessage(result.error.message));
        }
      }
    } catch (err) {
      console.log(err);
      setError("אירעה שגיאה בלתי צפויה");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      if (isSignUp && !latestTos?.version) {
        setError("לא ניתן לטעון את תנאי השימוש. אנא נסה שוב.");
        setIsSubmitting(false);
        return;
      }

      const result = await signInWithGoogle();
      if (result.error) {
        setError(getHebrewErrorMessage(result.error.message));
      } else if (result.user && isSignUp && latestTos) {
          storeToSAcceptance(latestTos.version);
      }
    } catch (err) {
      console.error(err);
      setError("אירעה שגיאה בהתחברות עם Google");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!formData.email) {
      setError("נא להזין כתובת אימייל");
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await resetPassword(formData.email);
      if (result.error) {
        setError(getHebrewErrorMessage(result.error.message));
      } else {
        setSuccessMessage("נשלח אימייל לאיפוס סיסמה. אנא בדוק את תיבת הדואר שלך");
        setShowForgotPassword(false);
      }
    } catch (err) {
      console.error(err);
      setError("אירעה שגיאה בשליחת אימייל לאיפוס סיסמה");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getHebrewErrorMessage = (errorMessage: string) => {
    if (errorMessage.includes("user-not-found")) {
      return "משתמש לא נמצא";
    }
    if (errorMessage.includes("wrong-password")) {
      return "סיסמה שגויה";
    }
    if (errorMessage.includes("email-already-in-use")) {
      return "כתובת האימייל כבר בשימוש";
    }
    if (errorMessage.includes("weak-password")) {
      return "הסיסמה חלשה מדי";
    }
    if (errorMessage.includes("invalid-email")) {
      return "כתובת אימייל לא תקינה";
    }
    return "אירעה שגיאה בהתחברות";
  };

  return (
    <div className="min-h-screen flex">
      {/* Right Side - Login Form */}
      <div className="w-full lg:w-2/5 flex items-center justify-center p-4 md:p-6 lg:p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8 md:mb-12">
            <h1 className="text-3xl md:text-4xl font-semibold mb-4 md:mb-6">
              <span style={{ color: "#1F1F68" }}>Legalix.</span>
              <span className="gradient-ai">AI</span>
            </h1>
            <h2 className="text-xl md:text-2xl font-medium mb-2 text-slate-600">התחבר לחשבון שלך</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6 text-zinc-400">
            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-right block font-medium">
                כתובת מייל
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="הכנס את האימייל שלך"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                required
                className="text-right h-12 md:h-14 px-3 md:px-4 py-2 md:py-3 rounded-lg border-gray-200 focus:border-blue-400 focus:ring-blue-400 placeholder:opacity-50"
                dir="rtl"
              />
            </div>

            {/* Full Name Field (only for signup) */}
            {isSignUp && (
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-right block font-medium">
                  שם מלא
                </Label>
                <Input
                  id="fullName"
                  type="text"
                  placeholder="השם המלא"
                  value={formData.fullName}
                  onChange={(e) => handleInputChange("fullName", e.target.value)}
                  required
                  className="text-right h-12 md:h-14 px-3 md:px-4 py-2 md:py-3 rounded-lg border-gray-200 focus:border-blue-400 focus:ring-blue-400 placeholder:opacity-50"
                  dir="rtl"
                />
              </div>
            )}

            {/* Password Field */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="password" className="text-right block font-medium">
                  סיסמה
                </Label>
                {!isSignUp && (
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(!showForgotPassword)}
                    className="text-blue-600 hover:text-blue-700 text-sm underline font-medium"
                  >
                    שכחת סיסמה?
                  </button>
                )}
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder={isSignUp ? "בחר סיסמא" : "הסיסמה שלך"}
                  value={formData.password}
                  onChange={(e) => handleInputChange("password", e.target.value)}
                  required
                  className="text-right h-12 md:h-14 px-3 md:px-4 py-2 md:py-3 rounded-lg border-gray-200 focus:border-blue-400 focus:ring-blue-400 pr-10 md:pr-12 placeholder:opacity-50"
                  dir="rtl"
                />
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                  {/* <Eye className="w-5 h-5 text-violet-950 opacity-50 flex-shrink-0 mt-0.5" /> */}
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Forgot Password Section */}
            {showForgotPassword && !isSignUp && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                <p className="text-sm text-gray-700 text-right">
                  הזן את כתובת האימייל שלך ונשלח לך קישור לאיפוס הסיסמה
                </p>
                <Button
                  type="button"
                  onClick={handlePasswordReset}
                  disabled={isSubmitting}
                  className="w-full h-10 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm"
                >
                  {isSubmitting ? "שולח..." : "שלח קישור לאיפוס סיסמה"}
                </Button>
              </div>
            )}

            {/* Error Message */}
            {error && <div className="text-red-500 text-sm text-right bg-red-50 p-3 rounded-md">{error}</div>}

            {/* Success Message */}
            {successMessage && <div className="text-green-600 text-sm text-right bg-green-50 p-3 rounded-md">{successMessage}</div>}

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full h-12 rounded-xl gradient-button text-white font-medium text-base hover:opacity-90 transition-opacity"
              disabled={isSubmitting}
              style={{
                background: "linear-gradient(90deg, #434AA7 0%, #4956D3 100%)",
              }}
            >
              {isSubmitting ? "מתחבר..." : isSignUp ? "הירשם" : "התחבר"}
            </Button>

            {/* Terms of Service notice (only for signup) */}
            {isSignUp && (
              <p className="text-xs text-gray-400 text-center leading-relaxed mt-2">
                בהרשמתך הינך מסכים ל{" "}
                <Link
                  to="/terms-of-service"
                  className="text-blue-500 hover:text-blue-600 underline"
                  target="_blank"
                >
                  תנאי השימוש
                </Link>
                {" "}ול{" "}
                <Link
                  to="/privacy-policy"
                  className="text-blue-500 hover:text-blue-600 underline"
                  target="_blank"
                >
                  מדיניות הפרטיות
                </Link>
              </p>
            )}

            {/* Separator */}
            <div className="relative flex items-center justify-center my-8">
              <div className="border-t border-gray-200 flex-1"></div>
              <span className="px-4 text-gray-500 text-sm font-medium">או</span>
              <div className="border-t border-gray-200 flex-1"></div>
            </div>

            {/* Social Sign In Buttons - Circular */}
            <div className="flex justify-center space-x-4">
              {/* Google Sign In */}
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={isSubmitting}
                className={`w-12 h-12 md:w-14 md:h-14 rounded-full bg-white border border-gray-200 flex items-center justify-center transition-shadow ${
                  isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'
                }`}
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
              </button>
            </div>

            {/* Toggle between Sign In and Sign Up */}
            <div className="text-center pt-4">
              <button
                type="button"
                onClick={() => {
                  setIsSignUp(!isSignUp);
                  setError(null);
                  setSuccessMessage(null);
                  setShowForgotPassword(false);
                  setFormData({ email: "", password: "", fullName: "" });
                }}
                className="text-blue-600 hover:text-blue-700 text-sm underline font-medium"
              >
                {isSignUp ? "כבר יש לך חשבון? התחבר כאן" : "אין לך חשבון? הירשם כאן"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Left Side - Image and Text Toasts */}
      <div className="hidden lg:flex lg:w-3/5 relative bg-gradient-to-br from-blue-100 to-blue-300 p-4 lg:p-8">
        <div className="flex flex-col justify-center items-center w-full relative">
          {/* Classical statue image */}
          <div
            className="rounded-lg mb-8 overflow-hidden max-w-full"
            style={{ width: "min(900px, 100%)", height: "min(900px, 70vh)" }}
          >
            <img
              src="roman_statue_with_laptop.png"
              alt="Roman statue with laptop representing legal technology"
              className="w-full h-full object-cover"
            />
          </div>

          {/* Text Toasts */}
          <div className="absolute top-20 right-20 bg-gradient-to-br from-blue-50 to-blue-100 backdrop-blur-sm rounded-2xl p-4 shadow-lg max-w-xs border border-blue-200/50">
            <div className="flex items-start gap-3">
              <p className="text-sm text-gray-700 text-right flex-1 leading-relaxed">
                מהם התנאים לקיומה של עוולת התקיפה לפי פקודת הנזיקין?
              </p>
              <HelpCircle className="w-5 h-5 text-violet-950 opacity-50 flex-shrink-0 mt-0.5" />
            </div>
          </div>

          <div className="absolute top-40 left-16 bg-gradient-to-br from-blue-50 to-blue-100 backdrop-blur-sm rounded-2xl p-4 shadow-lg max-w-xs border border-blue-200/50">
            <div className="flex items-start gap-3">
              <p className="text-sm text-gray-700 text-right flex-1 leading-relaxed">
                האם לפי כללי לשכת עורכי הדין, מותר לעורך דין להקליט שיחה עם עורך דין אחר?
              </p>
              <HelpCircle className="w-5 h-5 text-violet-950 opacity-50 flex-shrink-0 mt-0.5" />
            </div>
          </div>

          <div className="absolute bottom-32 right-12 bg-gradient-to-br from-blue-50 to-blue-100 backdrop-blur-sm rounded-2xl p-4 shadow-lg max-w-xs border border-blue-200/50">
            <div className="flex items-start gap-3">
              <p className="text-sm text-gray-700 text-right flex-1 leading-relaxed">האם שעות טיסה לחו"ל נחשבות ל"שעות עבודה"?</p>
              <HelpCircle className="w-5 h-5 text-violet-950 opacity-50 flex-shrink-0 mt-0.5" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
